import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Play, Pause, X, Link as LinkIcon, RefreshCw, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SetupBadge, ChecklistDialog } from "@/components/AdminStarterPack";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRIGGERS = [
  { v: "booking_created",        l: "Calendly · Booking created" },
  { v: "booking_rescheduled",    l: "Calendly · Booking rescheduled" },
  { v: "booking_cancelled",      l: "Calendly · Booking cancelled" },
  { v: "booking_approaching",    l: "Calendly · Booking approaching" },
  { v: "booking_no_show",        l: "Calendly · Booking no-show (marked manually)" },
  { v: "stripe_payment_done",    l: "Stripe · Payment succeeded" },
  { v: "checkout_abandoned",     l: "Stripe · Checkout abandoned" },
  { v: "subscription_cancelled", l: "Stripe · Subscription cancelled" },
  { v: "contact_created",        l: "CRM · New contact created" },
  { v: "reply_received",         l: "CRM · Contact replied (Gmail)" },
  { v: "assessment_completed",   l: "CRM · Assessment completed" },
  { v: "candidate_field_changed", l: "CRM · Candidate field changed (Stage/Status)" },
  { v: "manual_add",             l: "Manually enrolled" },
];
const ACTIONS = [
  { v: "send_email",         l: "Send email (template)" },
  { v: "wait",               l: "Wait (delay)" },
  { v: "wait_until",         l: "Wait until X before/after booking" },
  { v: "condition",          l: "Condition (branch)" },
  { v: "update_crm_stage",   l: "Update CRM stage" },
  { v: "assign_team_member", l: "Assign team member" },
  { v: "set_tag",            l: "Set tag" },
  { v: "stop_sequence",      l: "Stop sequence" },
];
const EXITS = [
  { v: "questionnaire_completed", l: "Questionnaire finished → STOP reminders" },
  { v: "reply_received",          l: "Reply detected in Gmail → PAUSE sequence" },
  { v: "unsubscribed",            l: "Unsubscribed → STOP" },
  { v: "manual_stop",             l: "Admin manual stop" },
];
const PIPELINES = [
  { v: "candidate", l: "Candidate (Careers)" },
  { v: "employer",  l: "Employer (Careers)" },
  { v: "relocator", l: "Relocator (Collective)" },
];
const CRM_STAGES = [
  "new_candidate", "contacted", "interviewed", "profile_complete", "marketing",
  "submitted", "interview_booked", "offer", "placed", "talent_pool",
];
const CONDITION_OPS = [
  { v: "exists",     l: "exists" },
  { v: "not_exists", l: "does not exist" },
  { v: "equals",     l: "equals" },
  { v: "not_equals", l: "does not equal" },
  { v: "contains",   l: "contains" },
  { v: "gte",        l: "≥" },
  { v: "lte",        l: "≤" },
];

const triggerLabel = (v) => TRIGGERS.find(t => t.v === v)?.l || v;

export function AutomationsPanel({ authHeaders }) {
  const [automations, setAutomations] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [checklists, setChecklists] = useState({});
  const [team, setTeam] = useState([]);
  const [perf, setPerf] = useState({});
  const [checklistFor, setChecklistFor] = useState(null);
  const [editing, setEditing] = useState(null);
  const [peopleFor, setPeopleFor] = useState(null);
  const [mapEditing, setMapEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, m, t, c, tm, pf] = await Promise.all([
        axios.get(`${API}/admin/automations`, { headers: authHeaders }),
        axios.get(`${API}/admin/automations/calendly-mappings`, { headers: authHeaders }),
        axios.get(`${API}/admin/email-hub/templates?include_archived=1`, { headers: authHeaders }),
        axios.get(`${API}/admin/automations/checklists`, { headers: authHeaders }).catch(() => ({ data: { checklists: {} } })),
        axios.get(`${API}/admin/workspace/team`, { headers: authHeaders }).catch(() => ({ data: { team: [] } })),
        axios.get(`${API}/admin/automations/performance`, { headers: authHeaders }).catch(() => ({ data: { performance: {} } })),
      ]);
      setAutomations(a.data.automations || []);
      setMappings(m.data.mappings || []);
      setTemplates(t.data.templates || []);
      setChecklists(c.data.checklists || {});
      setTeam(tm.data.team || []);
      setPerf(pf.data.performance || {});
    } catch (e) {
      toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`);
    }
  }, [authHeaders]);
  useEffect(() => { load(); }, [load]);

  const delAuto = async (id) => {
    if (!window.confirm("Delete this automation? Active runs will be stopped.")) return;
    await axios.delete(`${API}/admin/automations/${id}`, { headers: authHeaders });
    toast.success("Automation deleted."); load();
  };
  const toggleActive = async (a) => {
    if (!a.active) {
      const cl = checklists[a.id];
      if (cl && cl.blockers > 0) {
        setChecklistFor(a);
        toast.error(`Fix ${cl.blockers} setup item${cl.blockers === 1 ? "" : "s"} before activating.`);
        return;
      }
    }
    await axios.patch(`${API}/admin/automations/${a.id}`, { ...a, active: !a.active }, { headers: authHeaders });
    load();
  };
  const delMapping = async (uri) => {
    if (!window.confirm(`Remove mapping for ${uri}?`)) return;
    await axios.delete(`${API}/admin/automations/calendly-mappings/${encodeURIComponent(uri)}`, { headers: authHeaders });
    load();
  };

  return (
    <div className="space-y-8" data-testid="automations-panel">
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-bold text-[#0D2F4D]">Automations</h3>
            <p className="text-sm text-[#6F8FA8]">Trigger + action sequences, processed automatically by the background worker every minute.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                if (!window.confirm("Turn ON every automation? New signups & bookings will start receiving these email journeys automatically.")) return;
                try {
                  const r = await axios.post(`${API}/admin/automations/bulk-active?active=true`, {}, { headers: authHeaders });
                  toast.success(`${r.data.updated} automation${r.data.updated === 1 ? "" : "s"} turned on.`);
                  load();
                } catch (e) { toast.error(`Failed: ${e?.response?.data?.detail || e.message}`); }
              }}
              data-testid="automations-activate-all"
              className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
            >
              <Play className="h-4 w-4 mr-1"/>Turn all on
            </Button>
            <Button onClick={() => setEditing({ new: true, name: "", brand: "collective", trigger: { type: "booking_created", filters: {} }, steps: [], exits: ["reply_received", "questionnaire_completed"], active: true })}
              data-testid="automations-new" className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F]">
              <Plus className="h-4 w-4 mr-1"/>New automation
            </Button>
          </div>
        </div>
        <Card>
          <Table data-testid="automations-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Trigger</TableHead>
                <TableHead>Steps</TableHead>
                <TableHead>Runs</TableHead>
                <TableHead>Setup</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right w-[180px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {automations.length === 0 && (
                <TableRow><TableCell colSpan={7} className="py-8 text-center text-[#6F8FA8]">
                  No automations yet. Click <strong>New automation</strong> to wire one up.
                </TableCell></TableRow>
              )}
              {automations.map(a => (
                <TableRow key={a.id} data-testid={`automation-row-${a.id}`}>
                  <TableCell>
                    <div className="font-semibold text-[#0D2F4D]">{a.name}</div>
                    <div className="text-xs text-[#6F8FA8]"><Badge variant="outline" className="text-[10px] mr-1">{a.brand}</Badge>{a.description}</div>
                  </TableCell>
                  <TableCell className="text-xs">{triggerLabel(a.trigger?.type)}</TableCell>
                  <TableCell className="text-xs">{(a.steps || []).length}</TableCell>
                  <TableCell className="text-xs cursor-pointer hover:bg-[#F5F3EE] rounded" onClick={() => setPeopleFor(a)} data-testid={`automation-runs-${a.id}`} title="Click to see who's enrolled">
                    <span className="text-[#0D2F4D] font-bold">{a.runs_active}</span> live ·
                    <span className="text-emerald-700 ml-1">{a.runs_completed}</span> done ·
                    <span className="text-amber-700 ml-1">{a.runs_stopped}</span> stopped
                    <div className="text-[10px] text-[#6F8FA8] underline mt-0.5">View people</div>
                    {perf[a.id] && perf[a.id].sends > 0 && (
                      <div className="text-[10px] text-[#6F8FA8] mt-1 font-semibold" data-testid={`perf-${a.id}`}>
                        {perf[a.id].sends} sent · {perf[a.id].open_rate}% open · {perf[a.id].click_rate}% click
                        {perf[a.id].rebook_tracked ? ` · ${perf[a.id].rebook_rate}% rebook` : ""}
                      </div>
                    )}
                    {perf[a.id] && perf[a.id].sends === 0 && perf[a.id].enrolled > 0 && (
                      <div className="text-[10px] text-[#94A3B8] mt-1" data-testid={`perf-${a.id}`}>no sends yet</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <SetupBadge checklist={checklists[a.id]} onClick={() => setChecklistFor(a)} id={a.id} />
                  </TableCell>
                  <TableCell>
                    <Badge className={a.active ? "bg-emerald-600" : "bg-slate-400"}>{a.active ? "Active" : "Paused"}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => setPeopleFor(a)} data-testid={`automation-people-${a.id}`} title="See enrolled people"><Users size={12}/></Button>
                    <Button size="sm" variant="ghost" onClick={() => toggleActive(a)} data-testid={`automation-toggle-${a.id}`}>
                      {a.active ? <Pause size={12}/> : <Play size={12}/>}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing(a)} data-testid={`automation-edit-${a.id}`}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => delAuto(a.id)} data-testid={`automation-delete-${a.id}`} className="text-red-700 ml-1"><Trash2 size={12}/></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Calendly Mappings */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-bold text-[#0D2F4D] flex items-center gap-2"><LinkIcon size={16}/>Calendly mappings</h3>
            <p className="text-sm text-[#6F8FA8]">Map each Calendly event type → brand + pipeline + follow-up sequence. Reschedules automatically stop old reminders and start fresh ones.</p>
          </div>
          <div className="flex gap-2">
            <CalendlySyncButton authHeaders={authHeaders} onSynced={load} />
            <Button onClick={() => setMapEditing({ event_type_uri: "", event_label: "", brand: "collective", pipeline: "relocator", automation_id: "", active: true })}
              data-testid="calendly-mapping-new" className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F]">
              <Plus className="h-4 w-4 mr-1"/>New mapping
            </Button>
          </div>
        </div>
        <Card>
          <Table data-testid="calendly-mappings-table">
            <TableHeader>
              <TableRow>
                <TableHead>Event label</TableHead>
                <TableHead>Brand</TableHead>
                <TableHead>Pipeline</TableHead>
                <TableHead>Automation</TableHead>
                <TableHead>Event type URI</TableHead>
                <TableHead className="text-right w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mappings.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-[#6F8FA8]">
                  No Calendly mappings yet — add one per event (Candidate, Employer, Relocator).
                </TableCell></TableRow>
              )}
              {mappings.map(m => {
                const linked = automations.find(a => a.id === m.automation_id);
                return (
                  <TableRow key={m.event_type_uri} data-testid={`calendly-row-${m.event_type_uri}`}>
                    <TableCell className="font-semibold text-[#0D2F4D]">{m.event_label}</TableCell>
                    <TableCell><Badge variant="outline">{m.brand}</Badge></TableCell>
                    <TableCell><Badge variant="outline">{m.pipeline}</Badge></TableCell>
                    <TableCell className="text-xs">{linked?.name || <span className="text-[#94A3B8]">— none —</span>}</TableCell>
                    <TableCell className="text-xs font-mono text-[#6F8FA8] max-w-[240px] truncate">{m.event_type_uri}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => setMapEditing(m)}>Edit</Button>
                      <Button size="sm" variant="ghost" onClick={() => delMapping(m.event_type_uri)} className="text-red-700"><Trash2 size={12}/></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      </div>

      {editing && (
        <AutomationEditor initial={editing} authHeaders={authHeaders} templates={templates} team={team}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
      {checklistFor && (
        <ChecklistDialog automation={checklistFor} checklist={checklists[checklistFor.id]}
          onClose={() => setChecklistFor(null)} />
      )}
      {mapEditing && (
        <MappingEditor initial={mapEditing} automations={automations} authHeaders={authHeaders}
          onClose={() => setMapEditing(null)}
          onSaved={() => { setMapEditing(null); load(); }} />
      )}

      {peopleFor && (
        <PeopleModal automation={peopleFor} authHeaders={authHeaders}
          onClose={() => setPeopleFor(null)} onChanged={load} />
      )}
    </div>
  );
}


function CalendlySyncButton({ authHeaders, onSynced }) {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState("");
  const [tokenSaved, setTokenSaved] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/admin/automations/calendly-sync/status`, { headers: authHeaders })
      .then(r => setTokenSaved(!!r.data.token_saved)).catch(() => {});
  }, [open, authHeaders]);

  const sync = async () => {
    setSyncing(true); setResult(null);
    try {
      const r = await axios.post(`${API}/admin/automations/calendly-sync`, { token }, { headers: authHeaders });
      setResult(r.data);
      setTokenSaved(true); setToken("");
      toast.success(`Synced ${r.data.mappings_synced} mapping${r.data.mappings_synced === 1 ? "" : "s"} from Calendly.`);
      onSynced?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setSyncing(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="calendly-sync-open">
        <RefreshCw className="h-4 w-4 mr-1"/>Sync from Calendly
      </Button>
      {open && (
        <Dialog open={true} onOpenChange={(o) => !o && setOpen(false)}>
          <DialogContent data-testid="calendly-sync-dialog">
            <DialogHeader>
              <DialogTitle>Sync event types from Calendly</DialogTitle>
              <DialogDescription className="text-xs text-[#6F8FA8]">
                Pulls your active event types and wires one mapping per event — candidate, employer and
                relocator calls are matched automatically and linked to their booking journeys.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">
                  Personal Access Token {tokenSaved && <span className="text-emerald-600 normal-case tracking-normal">(saved — leave blank to reuse)</span>}
                </label>
                <Input type="password" value={token} onChange={(e) => setToken(e.target.value)}
                  placeholder={tokenSaved ? "••••••••  (stored)" : "eyJra…"} data-testid="calendly-sync-token"/>
                <p className="text-[11px] text-[#6F8FA8] mt-1">
                  Calendly → Integrations → API &amp; Webhooks → Personal Access Tokens → Get a token now.
                </p>
              </div>
              {result && (
                <div className="rounded-xl border border-[#E4E7EA] bg-[#FAFAF7] p-3 space-y-1" data-testid="calendly-sync-result">
                  <div className="text-xs font-bold text-[#0D2F4D]">{result.event_types_found} event types found · {result.mappings_synced} mappings synced</div>
                  {(result.mappings || []).map((m, i) => (
                    <div key={m.event_label || i} className="text-xs text-[#6F8FA8]">
                      {m.event_label} → <Badge variant="outline" className="text-[10px]">{m.pipeline}</Badge>
                      {m.linked_journey ? " · linked to booking journey" : ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
              <Button onClick={sync} disabled={syncing || (!token && !tokenSaved)}
                className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F]" data-testid="calendly-sync-run">
                {syncing ? "Syncing…" : "Sync now"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}


function StepParams({ step, idx, updStep, templates, brand, team }) {
  const p = step.params || {};
  const setP = (patch) => updStep(idx, { params: { ...p, ...patch } });
  const active = templates.filter(t => !t.archived);
  const brandTemplates = active.filter(t => t.brand === brand);
  const otherTemplates = active.filter(t => t.brand !== brand);

  if (step.type === "send_email") {
    const current = templates.find(t => t.key === p.template_key);
    const isArchived = !!current?.archived;
    const isMissing = !!p.template_key && !current;
    return (
    <div className="space-y-2">
      <Select value={p.template_key || ""} onValueChange={(v) => setP({ template_key: v })}>
        <SelectTrigger data-testid={`auto-step-tpl-${idx}`}><SelectValue placeholder="Choose a template…"/></SelectTrigger>
        <SelectContent className="max-h-72">
          {(isMissing || isArchived) && (
            <SelectItem value={p.template_key} data-testid={`auto-step-tpl-missing-${idx}`}>
              ⚠ {p.template_key} — {isMissing ? "not in library" : "archived"}
            </SelectItem>
          )}
          {brandTemplates.map(t => <SelectItem key={t.key} value={t.key}>{(t.description || t.subject || t.key)?.slice(0, 48)} · {t.key}</SelectItem>)}
          {otherTemplates.length > 0 && otherTemplates.map(t => <SelectItem key={t.key} value={t.key}>({t.brand}) {(t.description || t.subject || t.key)?.slice(0, 40)} · {t.key}</SelectItem>)}
        </SelectContent>
      </Select>
      {isMissing && (
        <p className="text-[11px] text-[#B91C1C] font-semibold" data-testid={`auto-step-tpl-warning-${idx}`}>
          This template key doesn't exist — the step will fail to send. Pick a current template.
        </p>
      )}
      {isArchived && (
        <p className="text-[11px] text-[#B45309] font-semibold" data-testid={`auto-step-tpl-archived-${idx}`}>
          This template is archived — it still sends, but consider re-pointing to a current template.
        </p>
      )}
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold shrink-0">Sender</span>
        <Select value={p.sender_admin_email || "__default__"}
          onValueChange={(v) => setP({ sender_admin_email: v === "__default__" ? "" : v })}>
          <SelectTrigger className="flex-1" data-testid={`auto-step-sender-${idx}`}><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="__default__">Brand default sender</SelectItem>
            {(team || []).map(m => <SelectItem key={m.email} value={m.email}>{m.name} · {m.email}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
    );
  }
  if (step.type === "wait") return (
    <div className="flex gap-2 items-center">
      <Input type="number" min="0" value={p.hours ?? 24} onChange={(e) => setP({ hours: Number(e.target.value) })} className="w-24" data-testid={`auto-step-wait-${idx}`}/>
      <span className="text-xs text-[#6F8FA8]">hours</span>
      <Input type="number" min="0" value={p.minutes ?? 0} onChange={(e) => setP({ minutes: Number(e.target.value) })} className="w-24" data-testid={`auto-step-wait-min-${idx}`}/>
      <span className="text-xs text-[#6F8FA8]">minutes</span>
    </div>
  );
  if (step.type === "wait_until") return (
    <div className="flex gap-2 items-center flex-wrap">
      <span className="text-xs text-[#6F8FA8]">Wait until</span>
      <Input type="number" min="0" step="0.5" value={p.offset_hours ?? 24} onChange={(e) => setP({ offset_hours: Number(e.target.value) })} className="w-24" data-testid={`auto-step-offset-${idx}`}/>
      <span className="text-xs text-[#6F8FA8]">hours</span>
      <Select value={p.direction || "before"} onValueChange={(v) => setP({ direction: v })}>
        <SelectTrigger className="w-28" data-testid={`auto-step-direction-${idx}`}><SelectValue/></SelectTrigger>
        <SelectContent>
          <SelectItem value="before">before</SelectItem>
          <SelectItem value="after">after</SelectItem>
        </SelectContent>
      </Select>
      <span className="text-xs text-[#6F8FA8]">the booking start time</span>
    </div>
  );
  if (step.type === "condition") return (
    <div className="space-y-2">
      <div className="flex gap-2 items-center flex-wrap">
        <Input value={p.field || ""} onChange={(e) => setP({ field: e.target.value })} placeholder="field (e.g. mapping.pipeline, product, amount)" className="flex-1 min-w-[180px]" data-testid={`auto-step-cond-field-${idx}`}/>
        <Select value={p.op || "exists"} onValueChange={(v) => setP({ op: v })}>
          <SelectTrigger className="w-40" data-testid={`auto-step-cond-op-${idx}`}><SelectValue/></SelectTrigger>
          <SelectContent>{CONDITION_OPS.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
        </Select>
        {!["exists", "not_exists"].includes(p.op || "exists") && (
          <Input value={p.value || ""} onChange={(e) => setP({ value: e.target.value })} placeholder="value" className="w-40" data-testid={`auto-step-cond-value-${idx}`}/>
        )}
      </div>
      <div className="flex gap-2 items-center">
        <span className="text-xs text-[#6F8FA8]">If false →</span>
        <Select value={p.on_false || "stop"} onValueChange={(v) => setP({ on_false: v })}>
          <SelectTrigger className="w-44" data-testid={`auto-step-cond-onfalse-${idx}`}><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="stop">Stop the run</SelectItem>
            <SelectItem value="skip">Skip next step(s)</SelectItem>
          </SelectContent>
        </Select>
        {p.on_false === "skip" && (
          <>
            <Input type="number" min="1" value={p.skip_count ?? 1} onChange={(e) => setP({ skip_count: Number(e.target.value) })} className="w-20" data-testid={`auto-step-cond-skip-${idx}`}/>
            <span className="text-xs text-[#6F8FA8]">step(s)</span>
          </>
        )}
      </div>
    </div>
  );
  if (step.type === "update_crm_stage") return (
    <Select value={p.stage || ""} onValueChange={(v) => setP({ stage: v })}>
      <SelectTrigger data-testid={`auto-step-stage-${idx}`}><SelectValue placeholder="Choose stage…"/></SelectTrigger>
      <SelectContent>{CRM_STAGES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
    </Select>
  );
  if (step.type === "assign_team_member") return (
    <Select value={p.assignee_email || ""} onValueChange={(v) => setP({ assignee_email: v })}>
      <SelectTrigger data-testid={`auto-step-assignee-${idx}`}><SelectValue placeholder="Choose a team member…"/></SelectTrigger>
      <SelectContent>
        {p.assignee_email && !(team || []).some(m => m.email === p.assignee_email) && (
          <SelectItem value={p.assignee_email}>⚠ {p.assignee_email} (not on the team roster)</SelectItem>
        )}
        {(team || []).map(m => <SelectItem key={m.email} value={m.email}>{m.name} · {m.email}</SelectItem>)}
      </SelectContent>
    </Select>
  );
  if (step.type === "set_tag") return (
    <Input value={p.tag || ""} onChange={(e) => setP({ tag: e.target.value })} placeholder="Tag name (e.g. Booking VIP)" data-testid={`auto-step-tag-${idx}`}/>
  );
  return null;
}


function PeopleModal({ automation, authHeaders, onClose, onChanged }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/automations/${automation.id}/runs?limit=500`, { headers: authHeaders });
      setRuns(r.data.runs || []);
    } catch (e) {
      toast.error(`Couldn't load enrolled people: ${e?.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, [automation.id, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const stopRun = async (runId) => {
    try {
      await axios.post(`${API}/admin/automations/runs/${runId}/stop`, {}, { headers: authHeaders });
      toast.success("Removed from automation.");
      load(); onChanged?.();
    } catch (e) { toast.error(`Failed: ${e?.response?.data?.detail || e.message}`); }
  };

  const shown = runs.filter(r => filter === "all" ? true : filter === "double" ? (r.also_in && r.also_in.length) : r.status === filter);
  const doubleCount = runs.filter(r => r.also_in && r.also_in.length).length;
  const counts = {
    running: runs.filter(r => r.status === "running").length,
    completed: runs.filter(r => r.status === "completed").length,
    stopped: runs.filter(r => r.status === "stopped").length,
  };
  const badge = (s) => s === "running" ? "bg-[#0D2F4D]" : s === "completed" ? "bg-emerald-600" : "bg-slate-400";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="automation-people-modal">
        <DialogHeader>
          <DialogTitle>Enrolled — {automation.name}</DialogTitle>
          <DialogDescription>{runs.length} total · {counts.running} live · {counts.completed} done · {counts.stopped} stopped{doubleCount ? ` · ⚠ ${doubleCount} double-enrolled` : ""}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1 flex-wrap">
          {["all", "running", "completed", "stopped", "double"].map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`people-filter-${f}`}
              className={`text-xs font-bold px-3 py-1 rounded-full border ${filter === f ? (f === "double" ? "bg-amber-600 text-white border-amber-600" : "bg-[#0D2F4D] text-white border-[#0D2F4D]") : (f === "double" ? "bg-white text-amber-700 border-amber-300" : "bg-white text-[#0D2F4D] border-[#D6DCE3]")}`}>
              {f === "all" ? "All" : f === "running" ? "Live" : f === "completed" ? "Done" : f === "stopped" ? "Stopped" : "⚠ Double-enrolled"}
            </button>
          ))}
        </div>
        <div className="max-h-[55vh] overflow-y-auto mt-2 divide-y divide-[#EEE9DE]">
          {loading ? <p className="text-sm text-[#6F8FA8] py-8 text-center">Loading…</p>
            : shown.length === 0 ? <p className="text-sm text-[#6F8FA8] py-8 text-center">No one here yet.</p>
            : shown.map((r) => (
              <div key={r.id} className="flex items-center gap-3 py-2.5" data-testid={`people-row-${r.id}`}>
                <Badge className={`${badge(r.status)} text-[10px]`}>{r.status === "running" ? "Live" : r.status === "completed" ? "Done" : "Stopped"}</Badge>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[#0D2F4D] truncate">
                    {(r.context?.first_name ? r.context.first_name + " · " : "")}{r.contact_email}
                  </div>
                  <div className="text-[11px] text-[#6F8FA8]">
                    Step {(r.current_step_index ?? 0) + 1} · started {r.started_at ? new Date(r.started_at).toLocaleDateString() : "—"}
                    {r.error ? ` · ⚠ ${r.error}` : ""}
                  </div>
                  {r.also_in && r.also_in.length > 0 && (
                    <div className="text-[11px] text-amber-700 font-semibold mt-0.5" data-testid={`people-alsoin-${r.id}`}>
                      ⚠ Also in: {r.also_in.join(", ")}
                    </div>
                  )}
                </div>
                {r.status === "running" && (
                  <Button size="sm" variant="ghost" className="text-red-700 text-xs" onClick={() => stopRun(r.id)} data-testid={`people-stop-${r.id}`}>Remove</Button>
                )}
              </div>
            ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AutomationEditor({ initial, authHeaders, templates, team, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const set = (patch) => setForm({ ...form, ...patch });
  const [saving, setSaving] = useState(false);

  const addStep = () => set({ steps: [...(form.steps || []), { type: "send_email", params: {} }] });
  const updStep = (idx, patch) => set({ steps: form.steps.map((s, i) => i === idx ? { ...s, ...patch } : s) });
  const rmStep = (idx) => set({ steps: form.steps.filter((_, i) => i !== idx) });
  const toggleExit = (v) => set({ exits: form.exits.includes(v) ? form.exits.filter(x => x !== v) : [...form.exits, v] });

  const save = async () => {
    if (!form.name?.trim()) return toast.error("Name required.");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), description: form.description || "", brand: form.brand,
        trigger: form.trigger, steps: form.steps || [], exits: form.exits || [], active: !!form.active,
      };
      if (initial.new) {
        await axios.post(`${API}/admin/automations`, payload, { headers: authHeaders });
        toast.success("Automation created.");
      } else {
        await axios.patch(`${API}/admin/automations/${initial.id}`, payload, { headers: authHeaders });
        toast.success("Automation saved.");
      }
      onSaved?.();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="automation-editor">
        <DialogHeader>
          <DialogTitle>{initial.new ? "New automation" : `Edit — ${initial.name}`}</DialogTitle>
          <DialogDescription className="text-xs text-[#6F8FA8]">Choose a trigger, stack actions, and pick exit rules — the worker handles the rest.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Name</label>
            <Input value={form.name} onChange={(e) => set({ name: e.target.value })} data-testid="auto-name" placeholder="Post-booking Candidate reminder"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Description</label>
            <Input value={form.description || ""} onChange={(e) => set({ description: e.target.value })} data-testid="auto-description"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Brand</label>
              <Select value={form.brand} onValueChange={(v) => set({ brand: v })}>
                <SelectTrigger data-testid="auto-brand"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="collective">The Ditch Collective</SelectItem>
                  <SelectItem value="careers">The Ditch Careers</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Trigger</label>
              <Select value={form.trigger?.type} onValueChange={(v) => set({ trigger: { ...form.trigger, type: v } })}>
                <SelectTrigger data-testid="auto-trigger"><SelectValue/></SelectTrigger>
                <SelectContent className="max-h-72">{TRIGGERS.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {form.trigger?.type === "booking_approaching" && (
            <div className="flex gap-2 items-center bg-[#FAFAF7] border border-[#E4E7EA] rounded-xl p-3">
              <span className="text-xs text-[#6F8FA8]">Enroll when a booking is within</span>
              <Input type="number" min="1" value={form.trigger?.filters?.lead_hours ?? 24}
                onChange={(e) => set({ trigger: { ...form.trigger, filters: { ...(form.trigger?.filters || {}), lead_hours: Number(e.target.value) } } })}
                className="w-24" data-testid="auto-trigger-leadhours"/>
              <span className="text-xs text-[#6F8FA8]">hours of starting</span>
            </div>
          )}
          {form.trigger?.type === "contact_created" && (
            <div className="bg-[#FAFAF7] border border-[#E4E7EA] rounded-xl p-3">
              <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Only enrol this type of lead</label>
              <Select
                value={form.trigger?.filters?.audience || "__any__"}
                onValueChange={(v) => set({ trigger: { ...form.trigger, filters: { ...(form.trigger?.filters || {}), audience: v === "__any__" ? "" : v } } })}
              >
                <SelectTrigger data-testid="auto-audience"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any new lead</SelectItem>
                  <SelectItem value="candidate">Candidates only</SelectItem>
                  <SelectItem value="employer">Employers only</SelectItem>
                  <SelectItem value="relocator">Relocation leads only</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#9AA7B2] mt-1">Routes new signups to the right welcome journey — e.g. candidate signups won't enter employer journeys.</p>
            </div>
          )}
          {form.trigger?.type === "candidate_field_changed" && (
            <div className="grid sm:grid-cols-2 gap-3 bg-[#FAFAF7] border border-[#E4E7EA] rounded-xl p-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">When this field changes</label>
                <Select
                  value={form.trigger?.filters?.field || "stage"}
                  onValueChange={(v) => set({ trigger: { ...form.trigger, filters: { ...(form.trigger?.filters || {}), field: v, value: "" } } })}
                >
                  <SelectTrigger data-testid="auto-fc-field"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stage">Stage</SelectItem>
                    <SelectItem value="status">Status</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">…to this value</label>
                <Select
                  value={form.trigger?.filters?.value || "__any__"}
                  onValueChange={(v) => set({ trigger: { ...form.trigger, filters: { ...(form.trigger?.filters || {}), value: v === "__any__" ? "" : v } } })}
                >
                  <SelectTrigger data-testid="auto-fc-value"><SelectValue/></SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="__any__">Any change</SelectItem>
                    {(form.trigger?.filters?.field === "status"
                      ? ["Active", "Keep Warm", "Not Progressing"]
                      : CRM_STAGES
                    ).map(v => <SelectItem key={v} value={v}>{v.replace(/_/g, " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold">Steps</label>
              <Button size="sm" variant="outline" onClick={addStep} data-testid="auto-add-step"><Plus size={12} className="mr-1"/>Add step</Button>
            </div>
            <div className="space-y-2">
              {(form.steps || []).length === 0 && <p className="text-sm text-[#6F8FA8]">No steps — a step-less automation just logs the trigger to the timeline.</p>}
              {(form.steps || []).map((s, i) => (
                <div key={i} className="border border-[#E4E7EA] rounded-xl p-3 space-y-2 bg-[#FAFAF7]" data-testid={`auto-step-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase font-bold text-[#6F8FA8]">Step {i + 1}</span>
                    <Select value={s.type} onValueChange={(v) => updStep(i, { type: v, params: {} })}>
                      <SelectTrigger className="flex-1" data-testid={`auto-step-type-${i}`}><SelectValue/></SelectTrigger>
                      <SelectContent>{ACTIONS.map(a => <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button size="sm" variant="ghost" onClick={() => rmStep(i)} className="text-red-700"><Trash2 size={12}/></Button>
                  </div>
                  <StepParams step={s} idx={i} updStep={updStep} templates={templates} brand={form.brand} team={team}/>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-2">Exit rules — stop the run automatically when…</label>
            <div className="space-y-1">
              {EXITS.map(e => (
                <label key={e.v} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.exits?.includes(e.v)} onChange={() => toggleExit(e.v)} data-testid={`auto-exit-${e.v}`}/>
                  {e.l}
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.active} onChange={(e) => set({ active: e.target.checked })} data-testid="auto-active"/>
            Active (unchecking pauses new enrollments)
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}><X className="h-4 w-4 mr-1"/>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F]" data-testid="auto-save">
            {saving ? "Saving…" : (initial.new ? "Create automation" : "Save changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function MappingEditor({ initial, automations, authHeaders, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const set = (patch) => setForm({ ...form, ...patch });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.event_type_uri?.trim() || !form.event_label?.trim()) return toast.error("Event URI + label required.");
    setSaving(true);
    try {
      await axios.post(`${API}/admin/automations/calendly-mappings`, form, { headers: authHeaders });
      toast.success("Mapping saved.");
      onSaved?.();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="mapping-editor">
        <DialogHeader>
          <DialogTitle>Calendly mapping</DialogTitle>
          <DialogDescription className="text-xs text-[#6F8FA8]">Route a Calendly event type to the right brand, pipeline and follow-up sequence.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Event label</label>
            <Input value={form.event_label} onChange={(e) => set({ event_label: e.target.value })} placeholder="Candidate Intro Call" data-testid="map-label"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Event type URI (from Calendly)</label>
            <Input value={form.event_type_uri} onChange={(e) => set({ event_type_uri: e.target.value })} placeholder="https://api.calendly.com/event_types/AAA…" data-testid="map-uri"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Brand</label>
              <Select value={form.brand} onValueChange={(v) => set({ brand: v })}>
                <SelectTrigger data-testid="map-brand"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="collective">The Ditch Collective</SelectItem>
                  <SelectItem value="careers">The Ditch Careers</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Pipeline</label>
              <Select value={form.pipeline} onValueChange={(v) => set({ pipeline: v })}>
                <SelectTrigger data-testid="map-pipeline"><SelectValue/></SelectTrigger>
                <SelectContent>{PIPELINES.map(p => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Follow-up automation</label>
            <Select value={form.automation_id || "__none__"} onValueChange={(v) => set({ automation_id: v === "__none__" ? "" : v })}>
              <SelectTrigger data-testid="map-automation"><SelectValue placeholder="(none)"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— none —</SelectItem>
                {automations.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.active} onChange={(e) => set({ active: e.target.checked })} data-testid="map-active"/>
            Active
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F]" data-testid="map-save">
            {saving ? "Saving…" : "Save mapping"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
